/**
 * The one rule for a windowed read (TMMAPP-0159 §0): `GET /v1/me/planned`, the cash flow
 * projection and any account, virtual account or envelope projection. tmm-api's window rule
 * (`WindowResolver`, design §8.1, MM-D-53) refuses a `from` before its today, because history is
 * the registers (`422 VALIDATION_FAILED {fields: {from}}`).
 *
 * - **A window that starts on or before today leaves `from` out**, so the server uses its own
 *   today in the person's zone. The app never sends a `from` it thinks is earlier than today.
 * - **`to` stays the window's end.** A window that ends before today asks nothing: no call.
 * - **The span is at most 12 months** (`WINDOW_MONTHS`), measured from the date the server uses as
 *   `from` (its today when `from` is left out): `to` is never past `from` + 12 months, by
 *   `addCivilMonths` (TMMAPP-0171 Part B).
 * - **A 422 on `from` or `to`** is retried once with `from` left out; a refusal naming `to` also
 *   caps `to` at the app's today + 12 months less a day (the app's today can be a day ahead of the
 *   server's around midnight). A second failure is the caller's to show, in its own section only.
 */
import { backendFields } from '@/api/errorDetails';
import { ErrorCodes, isApiError } from '@/api/errors';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useOptionalMe } from '@/session/meHooks';

/** tmm-api's longest window (`WindowResolver`, MM-D-53): `from` + 12 months. */
export const WINDOW_MONTHS = 12;

function civilOf(year: number, monthIndex: number, day: number): string {
  const date = new Date(Date.UTC(year, monthIndex, day));
  const y = String(date.getUTCFullYear()).padStart(4, '0');
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The civil date `months` calendar months from `civil` (negative goes back): the day is kept, or
 * clamped to the target month's last day (31 Jan + 1 month = 28/29 Feb). The product's one
 * civil-month rule.
 */
export function addCivilMonths(civil: string, months: number): string {
  const year = Number(civil.slice(0, 4));
  const monthIndex = Number(civil.slice(5, 7)) - 1 + months;
  const day = Number(civil.slice(8, 10));
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return civilOf(year, monthIndex, Math.min(day, lastDay));
}

/**
 * The civil date `days` calendar days from `civil` (negative goes back). The product's one
 * civil-day rule (TMMAPP-0174): calendar arithmetic only, no zone or DST shift.
 */
export function addCivilDays(civil: string, days: number): string {
  return civilOf(
    Number(civil.slice(0, 4)),
    Number(civil.slice(5, 7)) - 1,
    Number(civil.slice(8, 10)) + days,
  );
}

export interface CivilWindow {
  from: string;
  to: string;
}

/** What a windowed read sends: `to`, and `from` only when it is after today. */
export interface WindowQuery {
  from?: string;
  to: string;
}

/** The query for `window` on `today`, or null when the whole window is past (nothing to ask). */
export function windowQuery(window: CivilWindow, today: string): WindowQuery | null {
  if (window.to < today) return null;
  return window.from <= today ? { to: window.to } : { from: window.from, to: window.to };
}

/** A 422 that names the window's `from` or `to`. */
export function isWindowRefusal(error: unknown): boolean {
  if (!isApiError(error) || error.status !== 422) return false;
  if (error.code !== ErrorCodes.VALIDATION_FAILED) return false;
  const fields = backendFields(error);
  return 'from' in fields || 'to' in fields;
}

/**
 * Reads `window` through `read` by the rule above: `past` when the window has ended, one retry
 * without `from` on a window refusal (with `to` capped when the refusal names `to`), and any other
 * failure thrown to the caller.
 */
export async function readWindow<T>(
  window: CivilWindow,
  today: string,
  read: (query: WindowQuery) => Promise<T>,
  past: T,
): Promise<T> {
  const query = windowQuery(window, today);
  if (query === null) return past;
  try {
    return await read(query);
  } catch (error) {
    if (!isWindowRefusal(error)) throw error;
    if (!('to' in backendFields(error))) return read({ to: query.to });
    const cap = addCivilDays(addCivilMonths(today, WINDOW_MONTHS), -1);
    return read({ to: query.to < cap ? query.to : cap });
  }
}

/** The app's today: the person's `/me` zone, else the browser's. */
export function useAppToday(): string {
  const me = useOptionalMe();
  const zone = me?.user.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  return todayCivilInZone(zone);
}
